import React, { useState, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { getAccessToken } from '../Auth/AuthStore.ts'
import axios from 'axios'
import styles from './ImageUploadCanvas.module.css'
import sketchbook from '../../assets/icons/sketchbook.svg'
import TopicBanner from './TopicBanner'
import AdditionalQuestionsModal from './AdditionalQuestionsModal'
import AnalyzingModal from './AnalyzingModal'
import { AlertModal } from '../AlertModal'


const ImageUploadCanvas = ({ selectedTopic, onBack, healingCourseContext = null }) => {
  const [selectedFile, setSelectedFile] = useState(null)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [imageURL, setImageUrl] = useState(null)
  const [coords, setCoords] = useState(null)
  const [showQuestionsModal, setShowQuestionsModal] = useState(false)
  const [showAnalyzingModal, setShowAnalyzingModal] = useState(false)
  const [answers, setAnswers] = useState([])
  const [showAlert, setShowAlert] = useState(false)
  const [alertMessage, setAlertMessage] = useState('')
  
  const fileInputRef = useRef(null)
  const navigate = useNavigate()

  const DEFAULT_COORDS = {
    latitude: 37.505977,
    longitude: 126.956209,
    accuracy: null,
  }

  const handleFileSelect = (e) => {
    const file = e.target.files[0]
    if (file) {
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp']
      if (!validTypes.includes(file.type)) {
        setAlertMessage('PNG, JPG, JPEG, WEBP 파일만 업로드 가능합니다.')
        setShowAlert(true)
        return
      }

      setSelectedFile(file)
      
      // 미리보기 생성
      const reader = new FileReader()
      reader.onloadend = () => {
        setPreviewUrl(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()

    const file = e.dataTransfer.files[0]
    if (file) {
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp']
      if (!validTypes.includes(file.type)) {
        setAlertMessage('PNG, JPG, JPEG, WEBP 파일만 업로드 가능합니다.')
        setShowAlert(true)
        return
      }

      setSelectedFile(file)
      
      const reader = new FileReader()
      reader.onloadend = () => {
        setPreviewUrl(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const getPosition = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) {
        console.warn('브라우저가 geolocation을 지원하지 않아서 기본 좌표 사용')
        resolve(DEFAULT_COORDS)
        return
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          resolve({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          })
        },
        (err) => {
          console.warn('위치 정보 획득 실패, 기본 좌표 사용:', err?.message || err)
          resolve(DEFAULT_COORDS)
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 10000,
        }
      )
    })

  const handleComplete = useCallback(async () => {
    if (!selectedFile) {
      setAlertMessage('이미지를 선택해주세요.')
      setShowAlert(true)
      return
    }

    try {
      setUploading(true)

      /* 사용자 위치 전달 */
      let coords = null
      try {
        coords = await getPosition()
        setCoords(coords)
      } catch (geoErr) {
        console.warn('위치 정보 획득 실패:', geoErr?.message || geoErr)
      }

      const token = getAccessToken()
      if (!token) throw new Error('JWT 발급 실패: token 없음')

      /* url 요청 */
      const response = await axios.post(
        `${import.meta.env.VITE_API_BASE}/api/s3/presigned-url`,
        { mimeType: selectedFile.type },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      const presignedUrl = response.data?.data?.presignedUrl
      if (!presignedUrl) throw new Error('presignedUrl이 응답에 없습니다.')

      /* 업로드 */
      await axios.put(presignedUrl, selectedFile, {
        headers: { 'Content-Type': selectedFile.type },
      })

      /* 실제 URL 추출 */
      const pureImageUrl = presignedUrl.split('?')[0]
      setImageUrl(pureImageUrl)

      /* 후 질문 모달 */
      setShowQuestionsModal(true)
    } catch (err) {
      console.error('업로드 실패:', err)
      setAlertMessage('업로드 중 오류가 발생했습니다.')
      setShowAlert(true)
    } finally {
      setUploading(false)
    }
  }, [selectedFile])

  const getImageType = (topicId) => {
    const imageTypeMap = {
      'htp-house': 'ONE',
      'htp-tree': 'TWO',
      'htp-human': 'THREE',
      'pitr': 'FOUR',
    }
    return imageTypeMap[topicId] || 'ONE'
  }

  const handleQuestionsComplete = useCallback(
    async ({ title, followUpQuestions, answers }) => {
      setAnswers(answers)

      setShowQuestionsModal(false)
      setShowAnalyzingModal(true)

      const startTime = performance.now()

      try {
        const token = getAccessToken()
        if (!token) throw new Error('JWT 발급 실패: token 없음')

        const imageType = getImageType(selectedTopic?.id)

        const res = await axios.post(
          `${import.meta.env.VITE_API_BASE}/api/drawing-analysis`,
          {
            imageUrl: imageURL,
            title,
            imageType,
            latitude: coords?.latitude ?? null,
            longitude: coords?.longitude ?? null,
            followUpQuestions,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        )

        const analysisResult = res.data?.data

        const endTime = performance.now()
        const elapsedTime = ((endTime - startTime) / 1000).toFixed(1)

        setShowAnalyzingModal(false)
        setAlertMessage(`분석이 완료됐습니다. (소요 시간: ${elapsedTime}초)`)
        setShowAlert(true)

        navigate('/result', {
          state: {
            drawingData: previewUrl,
            answers,
            topicId: selectedTopic?.id,
            analysisResult,
            healingCourse: healingCourseContext,
          },
        })
      } catch (error) {
        console.error('질문 제출 실패:', error)
        setAlertMessage('분석중 오류가 발생했습니다. 다시 시도해주세요.')
        setShowAlert(true)
        setShowAnalyzingModal(false)
        setShowQuestionsModal(true)
      }
    },
    [navigate, imageURL, coords, selectedTopic, previewUrl]
  )


  return (
    <div className={styles.canvasWrapper}>
      <div className={styles.controls}>
        <button 
          className={styles.completeButton} 
          onClick={handleComplete}
          disabled={!selectedFile || uploading}
        >
          <i className="fas fa-check"></i>
          <span>완료</span>
        </button>
      </div>

      <div className={styles.canvasContainer}>
        <div className={styles.artboard}>
          <img
            src={sketchbook}
            alt="스케치북 배경"
            className={styles.canvasBackground}
          />

          {selectedTopic && (
            <div className={styles.bannerOnPaper}>
              <TopicBanner selectedTopic={selectedTopic} />
            </div>
          )}

          <div
            className={styles.uploadArea}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            {previewUrl ? (
              <div className={styles.previewContainer}>
                <img src={previewUrl} alt="업로드된 이미지" className={styles.previewImage} />
              </div>
            ) : (
              <div className={styles.uploadPlaceholder}>
                <div className={styles.uploadIcon}>
                  <i className="fas fa-cloud-upload-alt"></i>
                </div>
                <p className={styles.uploadText}>그림을 여기 드래그하거나 클릭하세요</p>
                <p className={styles.fileTypes}>PNG, JPG, JPEG, WEBP 파일 지원</p>
                <button className={styles.fileSelectButton}>
                  <i className="fas fa-plus"></i>
                  파일 선택
                </button>
                <p className={styles.warning}>
                  ※ 그림이외의 것은 포함되지 않도록 촬영해주세요.
                </p>
              </div>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </div>
      </div>

      <AdditionalQuestionsModal
        isOpen={showQuestionsModal}
        onClose={() => setShowQuestionsModal(false)}
        onComplete={handleQuestionsComplete}
        topicId={selectedTopic?.id}
        imageURL={imageURL}
        coords={coords}
      />

      <AnalyzingModal
        isOpen={showAnalyzingModal}
        onComplete={() => {}}
      />

      <AlertModal
        isOpen={showAlert}
        onClose={() => setShowAlert(false)}
        message={alertMessage}
      />
    </div>
  )
}

export default ImageUploadCanvas

